import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { useShowValidationErrors } from "~/common/hooks";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
} from "~/components/ui";
import { GetDataImportProjectsWithReferencesDocument } from "~/features/dataImportProjects/gqls";
import { useCreateDataImportProject } from "~/features/dataImportProjects/hooks/useCreateDataImportProject";
import {
  type CreateDataImportProjectFormData,
  zCreateDataImportProjectFormDataKeys,
} from "~/features/dataImportProjects/types";
import { useTenant } from "~/features/tenant/context";
import { v2Route } from "~/v2/routes";
import { RecordUpdateForEdit } from "./RecordUpdateForEdit";

type CreateDialogMode = "create" | "forEdit" | "bulkDeactivate";

interface DataImportProjectCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DataImportProjectCreateDialog({ open, onOpenChange }: DataImportProjectCreateDialogProps) {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const navigate = useNavigate();
  const [mode, setMode] = useState<CreateDialogMode>("create");
  const {
    register,
    reset,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<CreateDataImportProjectFormData>({
    defaultValues: {
      year: new Date().getFullYear().toString(),
      name: "",
      description: "",
      users: [],
      documentUrl: "",
    },
  });

  const showValidationErrors = useShowValidationErrors(setError, zCreateDataImportProjectFormDataKeys);
  const { submit, processing } = useCreateDataImportProject({
    refetchQueriesOnCreate: [{ query: GetDataImportProjectsWithReferencesDocument, variables: { tenantId } }],
    onSuccess: () => {
      reset();
      onOpenChange(false);
    },
    onValidationError: (error) => {
      showValidationErrors(error.fields);
    },
  });

  const handleOpenChange = (next: boolean) => {
    if (!next && processing) return;
    if (!next) {
      reset();
      setMode("create");
    }
    onOpenChange(next);
  };

  // Shared by the "for edit" and "bulk deactivate" tabs: close the dialog and navigate to the created DIP.
  const handleRecordUpdateCreated = (id: string | null) => {
    onOpenChange(false);
    setMode("create");
    if (id) {
      navigate(v2Route.dataImportProjects.detail.$buildPath({ params: { id } }));
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className={mode === "forEdit" || mode === "bulkDeactivate" ? "sm:max-w-3xl" : undefined}>
        <DialogHeader>
          <DialogTitle>{t("model.dataImportProject.modelName")}</DialogTitle>
        </DialogHeader>
        <Tabs value={mode} onValueChange={(v) => setMode(v as CreateDialogMode)}>
          <TabsList>
            <TabsTrigger value="create">{t("feature.recordUpdate.mode.create")}</TabsTrigger>
            <TabsTrigger value="forEdit">{t("feature.recordUpdate.mode.forEdit")}</TabsTrigger>
            <TabsTrigger value="bulkDeactivate">{t("feature.recordUpdate.mode.bulkDeactivate")}</TabsTrigger>
          </TabsList>
          <TabsContent value="create">
            <form id="dip-project-create-form" className="space-y-4" onSubmit={handleSubmit(submit)}>
              <div className="space-y-1.5">
                <label className="text-12-medium text-fg-2" htmlFor="dip-project-create-year">
                  {t("model.dataImportProject.year")}
                </label>
                <Input id="dip-project-create-year" type="number" min={1900} max={2100} {...register("year")} />
                {errors.year?.message && <p className="text-12-regular text-danger">{errors.year.message}</p>}
              </div>
              <div className="space-y-1.5">
                <label className="text-12-medium text-fg-2" htmlFor="dip-project-create-name">
                  {t("model.common.name")}
                </label>
                <Input
                  id="dip-project-create-name"
                  required
                  {...register("name", { required: t("ui.required") ?? "required" })}
                />
                {errors.name?.message && <p className="text-12-regular text-danger">{errors.name.message}</p>}
              </div>
              <div className="space-y-1.5">
                <label className="text-12-medium text-fg-2" htmlFor="dip-project-create-description">
                  {t("model.common.description")}
                </label>
                <Textarea
                  id="dip-project-create-description"
                  required
                  rows={5}
                  {...register("description", { required: t("ui.required") ?? "required" })}
                />
                {errors.description?.message && (
                  <p className="text-12-regular text-danger">{errors.description.message}</p>
                )}
              </div>
            </form>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={processing}>
                {t("ui.cancel")}
              </Button>
              <Button type="submit" form="dip-project-create-form" disabled={processing}>
                {t("ui.create")}
              </Button>
            </DialogFooter>
          </TabsContent>
          <TabsContent value="forEdit">
            <RecordUpdateForEdit onCreated={handleRecordUpdateCreated} />
          </TabsContent>
          <TabsContent value="bulkDeactivate">
            <RecordUpdateForEdit lifecycle="deactivate" onCreated={handleRecordUpdateCreated} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
